import { Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui'
import { useToast } from '@/hooks'
import { useCaseFactMutations } from '@/modules/projects/hooks'
import type { CaseFact } from '@/modules/projects/stores/types'
import { CaseFactEditor } from './CaseFactEditor'

type RowMode = 'reading' | 'editing' | 'confirmingDelete'

interface CaseFactRowProps {
  slug: string
  fact: CaseFact
}

export const CaseFactRow = ({ slug, fact }: CaseFactRowProps) => {
  const [mode, setMode] = useState<RowMode>('reading')
  const [isDeleting, setIsDeleting] = useState(false)
  const { updateCaseFact, deleteCaseFact } = useCaseFactMutations()
  const { errorToast } = useToast()
  const backToReading = () => setMode('reading')

  if (mode === 'editing') {
    return (
      <li className="py-2 last:pb-0">
        <CaseFactEditor
          initialText={fact.body}
          onSubmit={(body) =>
            updateCaseFact(slug, fact.id, body).then(backToReading)
          }
          onCancel={backToReading}
        />
      </li>
    )
  }

  const remove = () => {
    setIsDeleting(true)
    deleteCaseFact(slug, fact.id).catch((error) => {
      setIsDeleting(false)
      backToReading()
      errorToast('Couldn’t delete the fact', error)
    })
  }

  return (
    <li className="flex items-center gap-2 py-2 last:pb-0">
      <p
        className="min-w-0 flex-1 break-words text-sm"
        title={savedBy(fact) ?? undefined}
      >
        {fact.body}
      </p>
      {mode === 'confirmingDelete' ? (
        <>
          <Button
            theme="outline"
            size="xs"
            disabled={isDeleting}
            onClick={backToReading}
          >
            Cancel
          </Button>
          <Button
            theme="destructive"
            size="xs"
            isLoading={isDeleting}
            loadingText="Deleting..."
            onClick={remove}
          >
            Delete
          </Button>
        </>
      ) : (
        <>
          <Button
            theme="ghost"
            size="xs"
            isIcon
            aria-label="Edit fact"
            className="text-muted-foreground"
            onClick={() => setMode('editing')}
          >
            <Pencil />
          </Button>
          <Button
            theme="ghost"
            size="xs"
            isIcon
            aria-label="Delete fact"
            className="text-muted-foreground"
            onClick={() => setMode('confirmingDelete')}
          >
            <Trash2 />
          </Button>
        </>
      )}
    </li>
  )
}

const savedBy = (fact: CaseFact) => {
  if (fact.source === 'chat') return 'Saved from chat'
  return fact.createdByName && `Added by ${fact.createdByName}`
}
